import { Button, IconButton } from "@dropbox/dig-components/buttons";
import { UIIcon } from "@dropbox/dig-icons";
import {
  CloseLine,
  CopyLine,
  CreateStackLine,
  OpenLine,
} from "@dropbox/dig-icons/assets";
import ResultIcon from "@mirage/search/SearchResults/ResultIcon";
import { useTranslation } from "react-i18next";
import styles from "./DocumentPreviewHeader.module.css";

import type { ResultActionConfig } from "@mirage/search/SearchResults/ResultRow";
import type { SearchResult } from "@mirage/service-dbx-api";

type DocumentPreviewHeaderProps = {
  result: SearchResult;
  onClose: () => void;
  resultActionConfig?: ResultActionConfig;
};

export function DocumentPreviewHeader({
  result,
  onClose,
  resultActionConfig,
}: DocumentPreviewHeaderProps) {
  const { t } = useTranslation();

  const copyAction = resultActionConfig?.displayActions?.find(
    (action) => action.key === "copy",
  );
  const addToStackAction = resultActionConfig?.displayActions?.find(
    (action) => action.key === "add_to_stack",
  );
  const openAction = resultActionConfig?.launchAction;

  const isCopyAvailable = !!copyAction;
  const isAddToStackAvailable = !!addToStackAction;
  const isOpenAvailable = !!openAction;

  return (
    <div className={styles.drawerHeader}>
      <div className={styles.drawerLayout}>
        <div className={styles.customTile}>
          <div className={styles.tileStartAccessory}>
            <ResultIcon result={result} size="medium" />
          </div>
          <div className={styles.tileLabel}>{result.title}</div>
        </div>

        <div className={styles.buttonGroup}>
          {isCopyAvailable && (
            <Button
              variant="borderless"
              size="medium"
              withIconStart={<UIIcon src={CopyLine} />}
              tone="neutral"
              onClick={() => copyAction?.onClick?.("click")}
              className={styles.actionButton}
              disabled={copyAction?.disabled}
              {...(copyAction?.analyticsAttr || {})}
            >
              {copyAction?.title || t("copy_link")}
            </Button>
          )}

          {isAddToStackAvailable && (
            <Button
              variant="borderless"
              size="medium"
              withIconStart={<UIIcon src={CreateStackLine} />}
              tone="neutral"
              onClick={() => addToStackAction?.onClick?.("click")}
              className={styles.actionButton}
              disabled={addToStackAction?.disabled}
              {...(addToStackAction?.analyticsAttr || {})}
            >
              {addToStackAction?.title || t("add_to_stack")}
            </Button>
          )}

          {isOpenAvailable && (
            <Button
              variant="borderless"
              size="medium"
              withIconStart={<UIIcon src={OpenLine} />}
              tone="neutral"
              onClick={() => openAction?.onClick?.("click")}
              className={styles.openButton}
              disabled={openAction?.disabled}
              {...(openAction?.analyticsAttr || {})}
            >
              {openAction?.title ||
                t("search_inline_cta_search_result", {
                  appName: result.connectorInfo.displayName,
                })}
            </Button>
          )}

          <IconButton
            variant="borderless"
            onClick={onClose}
            className={styles.closeButton}
          >
            <UIIcon src={CloseLine} aria-label="Close" />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
